import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { FormGroup } from 'components/common/form';
import { defaultBlockData } from 'components/editor/blocks';
import { EditorBlockChoiceTasks } from 'components/editor/blocks/choiceTasks';
import { TextEditor } from 'components/editor/inputs';

import type { BlockMultipleChoice } from 'shared-types';
import type { BlockProps } from 'components/editor/blocks/EditorBlock';
import type { BlockChoiceTasksStateProps } from '../choiceTasks/EditorBlockChoiceTasks';

const EditorBlockMultipleChoice = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockMultipleChoice> & BlockChoiceTasksStateProps) => {
  const { editorLanguageReference } = stateProps;

  const { messages, serverConfig, theme } = useReduxState();

  const handleFeedbackPartiallyCorrectChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedbackPartiallyCorrect: html
          }
        }
      }
    });
  };

  const partialFeedbackForm = (
    <>
      <FormGroup
        label={
          <div
            className="result-label"
            data-tooltip={
              messages.editorBlockMultipleChoiceFeedbackPartiallyCorrectInfo
            }
          >
            <div className="result-label-icon">
              <Icon icon="check-circle" style={{ color: theme.warningColor }} />
            </div>
            {messages.editorBlockMultipleChoiceFeedbackPartiallyCorrect}
            <Icon
              icon="question-circle"
              style={{ fontSize: '1.4rem', marginLeft: 5 }}
            />
          </div>
        }
        maxWidth="100%"
      >
        {isAIPreview || isPreview ? (
          <RichTextValue
            html={
              data.locales[languageVersion].translatable
                .feedbackPartiallyCorrect || '-'
            }
            style={{
              backgroundColor: 'white',
              border: `1px solid ${theme.lightGreyDark}`,
              borderRadius: theme.inputBorderRadius,
              color: theme.textColorLight,
              padding: 10
            }}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable
                  ?.feedbackPartiallyCorrect || ''
              }
              style={{ marginTop: 5 }}
              translationHtml={
                data.locales[languageVersion].translatable
                  .feedbackPartiallyCorrect || ''
              }
            >
              <TextEditor
                backgroundColor="white"
                colorPickerEnabled={
                  serverConfig.multipleChoiceUseAltFeedbackStyle
                }
                content={
                  data.locales[languageVersion].translatable
                    .feedbackPartiallyCorrect || ''
                }
                onChange={handleFeedbackPartiallyCorrectChange}
                placeholder={messages.commonFeedbackPlaceholder}
                showBorder={true}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <style jsx>{`
        .result-label {
          align-items: center;
          display: flex;
        }
        .result-label-icon {
          font-size: 1.1rem;
          margin-right: 5px;
          position: relative;
          top: 1px;
        }
      `}</style>
    </>
  );

  return (
    <EditorBlockChoiceTasks
      blockId={blockId}
      blockName={messages.blocksMultipleChoice}
      data={data}
      defaultPointsWeight={defaultBlockData.multiplechoice.pointsWeight}
      isAIPreview={isAIPreview}
      isPreview={isPreview}
      languageVersion={languageVersion}
      partialFeedbackForm={partialFeedbackForm}
      taskQuestionPlaceHolder={
        messages.editorBlockMultipleChoiceQuestionPlaceholder
      }
      updateData={updateData}
      {...stateProps}
    />
  );
};

export default EditorBlockMultipleChoice;
